import { useEffect, useState } from 'react';
import { Badge, Button, Card, Group, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import type { ExpeditorAgreementDto } from '@expeditor/shared';
import { agreementsApi } from '../../shared/api/agreements';
import { DateField } from '../../shared/ui/DateField';

/** Менеджерская карточка «Договора ТЭО» клиента: список + добавление + архив. */
export function AgreementsCard({ clientId }: { clientId: string }) {
  const [items, setItems] = useState<ExpeditorAgreementDto[]>([]);
  const [number, setNumber] = useState('');
  const [signedDate, setSignedDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => agreementsApi.listForClient(clientId).then(setItems).catch(() => {});
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const add = async () => {
    setError(null);
    setBusy(true);
    try {
      await agreementsApi.createForClient(clientId, {
        number,
        signedDate: new Date(signedDate).toISOString(),
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        subject: subject || undefined,
      });
      setNumber('');
      setSignedDate('');
      setValidUntil('');
      setSubject('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось добавить договор');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id: string) => {
    if (!confirm('Архивировать договор? Он перестанет быть действующим.')) return;
    await agreementsApi.archive(clientId, id).catch(() => {});
    await reload();
  };

  return (
    <Card withBorder>
      <Title order={4} mb="sm">
        Договора ТЭО
      </Title>
      <Stack>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Номер</Table.Th>
              <Table.Th>Дата</Table.Th>
              <Table.Th>Действует до</Table.Th>
              <Table.Th>Предмет</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((a) => (
              <Table.Tr key={a.id}>
                <Table.Td>{a.number}</Table.Td>
                <Table.Td>{a.signedDate.slice(0, 10)}</Table.Td>
                <Table.Td>{a.validUntil ? a.validUntil.slice(0, 10) : 'бессрочно'}</Table.Td>
                <Table.Td>{a.subject ?? '—'}</Table.Td>
                <Table.Td>
                  {a.isActive ? (
                    <Badge color="green">действует</Badge>
                  ) : (
                    <Badge color="gray">{a.archivedAt ? 'в архиве' : 'истёк'}</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {a.isActive && (
                    <Button size="xs" variant="subtle" color="red" onClick={() => archive(a.id)}>
                      В архив
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm">
                    Договоров ТЭО пока нет
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        <Group align="flex-end">
          <TextInput
            label="Номер договора"
            value={number}
            onChange={(e) => setNumber(e.currentTarget.value)}
            required
          />
          <DateField label="Дата заключения" value={signedDate} onChange={setSignedDate} />
          <DateField label="Действует до" value={validUntil} onChange={setValidUntil} />
          <TextInput
            label="Предмет"
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button onClick={add} loading={busy} disabled={!number || !signedDate}>
            Добавить
          </Button>
        </Group>
        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
